// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)
/* eslint-disable @typescript-eslint/no-floating-promises, @typescript-eslint/no-misused-promises, react-hooks/exhaustive-deps, react-hooks/immutability */

import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Button, Loader } from "semantic-ui-react";

import _ from "lodash";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { useAuth0 } from "../../../../auth/auth0";

import FieldOutlineTable from "../FieldOutline/FieldOutlineTable";
import FieldCsvUpload from "./FieldCsvUpload";

import * as fieldActions from "../../../../redux/actions/fieldActions";
import { getExistingFlightsPlotsInField } from "../../../../redux/actions/flightsPlotsActions";
import { setPlots, cutImage } from "../../../Lumber/OrthoViewer/orthoControlFunctions";
import DeleteConfirmationModal from "../FieldWizard/deleteModal";
import { validateForm } from "./Validation";

const FieldLayout = ({
	fieldId,
	fromViewer,
	handleCancel,
	navigateToNextStep,
	navigateBack,
	handleDeleteField,
	isDeleting,
}) => {
	const dispatch = useDispatch();
	const { getTokenSilently } = useAuth0();

	const [layoutInfo, setLayoutInfo] = useState(null);
	const [fieldOutlineData, setFieldOutlineData] = useState(null);
	const [csvData, setCsvData] = useState(null);
	const [fieldLayoutCsvFile, setFieldLayoutCsvFile] = useState(null);
	const [fileName, setFileName] = useState(null);
	const [fieldLayoutInspection, setFieldLayoutInspection] = useState(null);
	const clientId = useSelector((state) => (state.clients ? state.clients.currentId : null));

	//-- UI Control
	const [loading, setLoading] = useState(true);
	const [saving, setSaving] = useState(false);

	useEffect(() => {
		if (fieldId) {
			setLoading(true);
			getLayoutInfo();
		}
	}, [fieldId]);

	async function getLayoutInfo() {
		const accessToken = await getTokenSilently();
		dispatch(fieldActions.getFieldLayoutInfo(fieldId, clientId, accessToken))
			.then((res) => {
				setLayoutInfo(res.layoutInfo);
				setCsvInfo(res.outlineData);
				setFieldOutlineData(res.outlineData);
				setLoading(false);
			})
			.catch((err) => {
				setLoading(false);
				console.log(err);
				toast.error("Failed to load Field Layout data.");
			});
	}

	function setCsvInfo(fieldOutlineData) {
		if (fieldOutlineData && fieldOutlineData.length > 0) {
			const fieldName = fieldOutlineData[0].field;

			setFileName(`${fieldName}_FieldOutline_${moment().format("YYYYMMDD_hhmma")}.csv`);

			setCsvData(
				fieldOutlineData.map((fod) => {
					return {
						Field: fod.field,
						Range: fod.range,
						Column: fod.column,
						Company: fod.company,
						Trial: fod.trial,
						Treatment: fod.trialTreatmentId,
						Plot: fod.plot,
						Rep: fod.rep,
						Seed: fod.seed,
						RecId: fod.recId,
					};
				}),
			);
		}
	}

	async function editFieldLayout(continueToNextPage) {
		if (!fieldLayoutCsvFile) {
			handleNoLayoutFile(continueToNextPage);
			return;
		}
		let validate = validateForm(fieldLayoutInspection);
		if (validate?.error) {
			toast.error("The CSV uploaded contained errors. Please fix the CSV file and try again.");
			return;
		}
		setSaving(true);
		const accessToken = await getTokenSilently();
		dispatch(fieldActions.editFieldLayout(layoutInfo, fieldLayoutCsvFile, accessToken))
			.then(() => {
				if (fieldLayoutInspection?.areValid) {
					toast.info("Preparing new trial images.");
					handleTrialCutting(accessToken);
				} else {
					setSaving(false);
				}

				toast.success("Field Layout updated successfully.");

				if (continueToNextPage) {
					navigateToNextStep();
				} else {
					getLayoutInfo();
				}
			})
			.catch((err) => {
				setSaving(false);
				console.log(err);
				toast.error("Failed to edit Field Layout data.");
			});
	}

	function handleNoLayoutFile(continueToNextPage) {
		if (continueToNextPage) {
			navigateToNextStep();
		} else {
			toast.warn("Please select a field layout CSV to upload.");
		}
	}

	function getCsvFile(updatedFieldLayoutCsvFile) {
		setFieldLayoutCsvFile(updatedFieldLayoutCsvFile);
	}

	function handleTrialCutting(accessToken) {
		dispatch(fieldActions.getFlightsWithFieldOrTrial(clientId, fieldId, null, accessToken))
			.then(async ({ flights }) => {
				let flightOrthoCombinations = [];

				_.map(flights, ({ flightId, orthoImageTypeId, sensorId }) => {
					if (
						!flightOrthoCombinations.some((flight) => {
							return (
								flight.flightId === flightId &&
								flight.orthoImageTypeId === orthoImageTypeId &&
								flight.sensorId === sensorId
							);
						})
					) {
						flightOrthoCombinations.push({
							flightId: flightId,
							orthoImageTypeId: orthoImageTypeId,
							sensorId: sensorId,
						});
					}
				});

				let existingFlights = _.uniqBy(flightOrthoCombinations, "flightId");

				await Promise.all(
					existingFlights.map(async ({ flightId }, index) => {
						await dispatch(getExistingFlightsPlotsInField(flightId, fieldId, clientId, accessToken)).then((res) => {
							existingFlights[index].plots = _.map(res.plots, (plot) => {
								return {
									id: plot.plotId,
									type: "Feature",
									properties: {
										type: "plot",
										field: plot.fieldId,
										fieldName: plot.fieldName,
										trial: plot.trialId,
										trialName: `${plot.companyName} - ${plot.trialName}`,
										fieldColWidth: plot.plotColumnWidth,
										locked: true,
									},
									geometry: {
										type: "Polygon",
										coordinates: [
											[
												[plot.llLong, plot.llLat],
												[plot.ulLong, plot.ulLat],
												[plot.urLong, plot.urLat],
												[plot.lrLong, plot.lrLat],
												[plot.llLong, plot.llLat],
											],
										],
									},
								};
							});
						});
					}),
				);

				await Promise.all(
					existingFlights.map(async ({ flightId, plots }) => {
						setPlots(plots);

						let orthoImageTypes = _.map(
							_.filter(flightOrthoCombinations, { flightId: flightId }),
							({ orthoImageTypeId, sensorId }) => {
								return {
									orthoImageTypeId: orthoImageTypeId,
									sensorId: sensorId,
								};
							},
						);

						await cutImage(clientId, flightId, orthoImageTypes, accessToken, dispatch, true, true, [{ id: fieldId }]);
					}),
				);

				setSaving(false);
			})
			.catch(() => {
				// Will enter if field isn't flown
				setSaving(false);
			});
	}

	return loading ? (
		<Loader active inline="centered" />
	) : (
		<div>
			<div style={{ display: "flex", width: "100%", marginBottom: 50 }}>
				<Button
					id="form-button-back"
					color="black"
					compact
					content={"Back"}
					disabled={saving || isDeleting}
					onClick={() => {
						navigateBack();
					}}
				/>
				<div style={{ flexGrow: 1 }} />
				{fromViewer && (
					<Button
						id="form-button-cancel"
						type="button"
						compact
						disabled={saving}
						onClick={() => {
							setSaving(true);
							handleCancel();
						}}
					>
						Cancel
					</Button>
				)}
				{!fromViewer && (
					<DeleteConfirmationModal
						handleDeleteField={handleDeleteField}
						isDeleting={isDeleting}
						disabled={saving || isDeleting}
						loading={isDeleting}
					/>
				)}
				<Button
					id="form-button-save"
					color="blue"
					compact
					content={"Save"}
					onClick={() => editFieldLayout(false)}
					disabled={saving || isDeleting}
					loading={saving}
				/>
				<Button
					id="form-button-save-and-continue"
					primary
					compact
					content={"Save & Continue"}
					onClick={() => editFieldLayout(true)}
					disabled={saving || isDeleting}
					loading={saving}
				/>
			</div>
			<FieldCsvUpload
				fieldInfo={layoutInfo}
				fieldFileName={fileName}
				fieldCsvData={csvData}
				saving={saving}
				getCsvFileCallback={getCsvFile}
				setFieldLayoutInspection={setFieldLayoutInspection}
			/>
			<FieldOutlineTable fieldOutlineData={fieldOutlineData} />
		</div>
	);
};

FieldLayout.propTypes = {
	fieldId: PropTypes.string,
	fromViewer: PropTypes.bool,
	handleCancel: PropTypes.func,
	navigateToNextStep: PropTypes.func,
	navigateBack: PropTypes.func,
	handleDeleteField: PropTypes.func,
	isDeleting: PropTypes.bool,
};

export default FieldLayout;
